import { useMemo, useState } from "react";

import { formatMinor, todayLocal } from "../lib/money";
import type { AccountBalance, Category, IncomeSource, LedgerTransaction, OperationDraft } from "../lib/types";
import { TransactionRow, typeLabel } from "../components/TransactionRow";

interface HistoryViewProps {
  transactions: LedgerTransaction[];
  accounts: AccountBalance[];
  categories: Category[];
  incomeSources: IncomeSource[];
  onEdit: (transactionId: string, draft: OperationDraft) => void;
  onRefund: (draft: OperationDraft) => void;
  onDelete: (transactionId: string, deleted: boolean) => Promise<void>;
}

export function HistoryView({ transactions, accounts, categories, incomeSources, onEdit, onRefund, onDelete }: HistoryViewProps) {
  const [filter, setFilter] = useState<"all" | "expense" | "income" | "transfer" | "trash">("all");
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => transactions.filter((transaction) => {
    const matchesTrash = filter === "trash" ? Boolean(transaction.deletedAt) : !transaction.deletedAt;
    const matchesType = filter === "all" || filter === "trash" || transaction.type === filter;
    const haystack = `${transaction.description ?? ""} ${transaction.counterparty ?? ""} ${transaction.originalText ?? ""}`.toLocaleLowerCase("ru");
    return matchesTrash && matchesType && (!query || haystack.includes(query.toLocaleLowerCase("ru")));
  }), [filter, query, transactions]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = filtered.find((transaction) => transaction.id === selectedId) ?? filtered[0] ?? null;

  return (
    <section className="view is-visible">
      <div className="history-toolbar panel">
        <div className="search-wrap"><span>⌕</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти операцию" /></div>
        <div className="filter-row">
          {(["all", "expense", "income", "transfer", "trash"] as const).map((value) => (
            <button key={value} className={`filter-button ${filter === value ? "is-active" : ""}`} type="button" onClick={() => setFilter(value)}>
              {({ all: "Все операции", expense: "Расходы", income: "Доходы", transfer: "Переводы", trash: "Корзина" })[value]}
            </button>
          ))}
        </div>
      </div>
      <div className="history-layout">
        <section className="history-list-panel panel">
          <div className="section-head history-heading"><div><p className="panel-label">Движение денежных средств</p><h2>История ДДС</h2></div><span className="operation-count">{filtered.length} операций</span></div>
          <div className="history-list">
            {filtered.map((transaction) => <TransactionRow key={transaction.id} transaction={transaction} accounts={accounts} asButton active={transaction.id === selected?.id} onClick={() => setSelectedId(transaction.id)} />)}
            {!filtered.length && <div className="empty-state"><span>Ничего не найдено</span><small>Измените фильтр или запрос</small></div>}
          </div>
        </section>
        <aside className="details-panel panel">
          {selected ? <TransactionDetails transaction={selected} accounts={accounts} categories={categories} incomeSources={incomeSources} onEdit={() => onEdit(selected.id, transactionToDraft(selected))} onRefund={selected.type === "expense" && !selected.deletedAt && remainingRefundable(selected, transactions) > 0n ? () => onRefund(refundDraft(selected, transactions)) : undefined} onDelete={() => onDelete(selected.id, !selected.deletedAt)} /> : <div className="detail-empty"><p>Выберите операцию,<br />чтобы увидеть детали</p></div>}
        </aside>
      </div>
    </section>
  );
}

function TransactionDetails({ transaction, accounts, categories, incomeSources, onEdit, onRefund, onDelete }: {
  transaction: LedgerTransaction;
  accounts: AccountBalance[];
  categories: Category[];
  incomeSources: IncomeSource[];
  onEdit: () => void;
  onRefund?: () => void;
  onDelete: () => Promise<void>;
}) {
  const negative = transaction.entries.find((entry) => entry.amountMinor < 0n);
  const positive = transaction.entries.find((entry) => entry.amountMinor > 0n);
  const accountName = (id?: string) => accounts.find((account) => account.accountId === id)?.name ?? "—";
  const accountLine = transaction.type === "transfer" ? `${accountName(negative?.accountId)} → ${accountName(positive?.accountId)}` : accountName((negative ?? positive)?.accountId);
  const classification = transaction.type === "expense" || transaction.type === "refund"
    ? categories.find((category) => category.id === transaction.categoryId)?.name
    : transaction.type === "income"
      ? incomeSources.find((source) => source.id === transaction.incomeSourceId)?.name
      : "Не участвует в доходах и расходах";
  return (
    <>
      <span className="detail-kicker">{new Date(`${transaction.occurredOn}T00:00:00`).toLocaleDateString("ru-RU")}</span>
      <h2>{transaction.description || typeLabel(transaction.type)}</h2>
      <p className={`detail-amount ${transaction.type}`}>{formatMinor(transaction.type === "expense" ? -transaction.amountMinor : transaction.amountMinor, transaction.type === "income" || transaction.type === "refund")}</p>
      <div className="detail-row"><span>Тип</span><strong>{typeLabel(transaction.type)}</strong></div>
      <div className="detail-row"><span>Счёт</span><strong>{accountLine}</strong></div>
      <div className="detail-row"><span>Категория / источник</span><strong>{classification ?? "—"}</strong></div>
      {transaction.originalText && <div className="detail-row"><span>Исходная фраза</span><strong className="source-phrase">«{transaction.originalText}»</strong></div>}
      <div className="detail-actions">
        <button className="secondary-button" disabled={Boolean(transaction.deletedAt)} type="button" onClick={onEdit}>Исправить</button>
        {onRefund && <button className="secondary-button" type="button" onClick={onRefund}>Оформить возврат</button>}
        <button className={transaction.deletedAt ? "secondary-button" : "danger-button"} type="button" onClick={() => void onDelete()}>{transaction.deletedAt ? "Восстановить" : "Удалить"}</button>
      </div>
    </>
  );
}

function refundDraft(expense: LedgerTransaction, transactions: LedgerTransaction[]): OperationDraft {
  const purchaseAccount = expense.entries.find((entry) => entry.amountMinor < 0n)?.accountId ?? "";
  const remainingMinor = remainingRefundable(expense, transactions);

  return {
    type: "refund",
    amountMinor: remainingMinor,
    occurredOn: todayLocal(),
    accountId: purchaseAccount,
    categoryId: null,
    incomeSourceId: null,
    refundOfTransactionId: expense.id,
    description: `Возврат: ${expense.description || "расход"}`,
    counterparty: expense.counterparty,
    originalText: null,
    confidence: "Высокая",
  };
}

function remainingRefundable(expense: LedgerTransaction, transactions: LedgerTransaction[]): bigint {
  const refundedMinor = transactions.reduce(
    (sum, transaction) => transaction.type === "refund" && !transaction.deletedAt && transaction.refundOfTransactionId === expense.id
      ? sum + transaction.amountMinor
      : sum,
    0n,
  );
  return expense.amountMinor - refundedMinor;
}

function transactionToDraft(transaction: LedgerTransaction): OperationDraft {
  const negative = transaction.entries.find((entry) => entry.amountMinor < 0n);
  const positive = transaction.entries.find((entry) => entry.amountMinor > 0n);
  const primary = transaction.type === "income" || transaction.type === "refund" ? positive : negative ?? positive;
  return {
    type: transaction.type,
    amountMinor: transaction.amountMinor,
    occurredOn: transaction.occurredOn,
    accountId: primary?.accountId ?? "",
    toAccountId: transaction.type === "transfer" ? positive?.accountId : null,
    direction: transaction.type === "opening_balance" || transaction.type === "balance_adjustment" ? (primary?.amountMinor ?? 0n) >= 0n ? 1 : -1 : null,
    categoryId: transaction.categoryId,
    incomeSourceId: transaction.incomeSourceId,
    refundOfTransactionId: transaction.refundOfTransactionId,
    description: transaction.description,
    counterparty: transaction.counterparty,
    originalText: transaction.originalText,
  };
}
